import { Box, IconButton, Paper, SvgIcon } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import { StoreType } from '../../../reducers';
import { useSelector } from 'react-redux';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { ReaderContext } from '../../Reader/ReaderContext';
import ReaderDetails from './ReaderDetails';
import { OnboardingHint } from '../../OnboardingHint';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { onboardingHints } from '../../../constants';

export const CONTENT_REAPPEAR_DELAY = 150;

const ReaderDetailsWrapper: React.FC = () => {
  const {
    mindItemId,
    isReaderDetailsCollapsed,
    setReaderDetailsCollapsed,
    fullScreenMode,
    onboardingStepCounter,
  } = useContext(ReaderContext);

  const mindItem = useSelector((state: StoreType) => selectMindItem(state, mindItemId));
  const { t } = useTranslation();
  const [buttonDimensions, setButtonDimensions] = useState({ width: 0, height: 0 });
  const [isContentHidden, setContentHidden] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      const { width, height } = buttonRef.current.getBoundingClientRect();
      setButtonDimensions({ width, height });
    }
  }, [buttonRef.current]);

  const truncate = () => {
    if (setReaderDetailsCollapsed) {
      setReaderDetailsCollapsed(true);
    }
    setContentHidden(true);
  };

  const expand = () => {
    if (setReaderDetailsCollapsed) {
      setReaderDetailsCollapsed(false);
    }
    setTimeout(() => setContentHidden(false), CONTENT_REAPPEAR_DELAY);
  };

  if (!mindItem) return null;

  const expandedStyle = {
    height: '100%',
    width: customSizes.readerDetails.width,
  };

  const readerDetailsWrapper = <Box sx={{
    paddingY: customSizes.readerPage.paddingY,
    ...(fullScreenMode ? {
      display: 'none',
    } : {}),
  }}>
    <Paper 
      elevation={2}
      sx={{
        borderRadius: spaces.sm,
        color: palette.text.primary,
        display: 'flex',
        gap: spaces.xxs,
        flexDirection: 'column',
        ...(isReaderDetailsCollapsed ? { ...buttonDimensions } : expandedStyle),
        transition: 'width 0.25s, height 0.25s',
        transitionTimingFunction: 'cubic-bezier(0.1, 0.57, 0.1, 1)',
        paddingBottom: spaces.sm,
      }}
    >
      <ReaderDetails
        topRightAction={{
          icon: <SvgIcon component={getMuiIcon('arrowLeft')} />,
          onClick: truncate,
          buttonSx: {
            marginRight: `-${spaces.sm}`
          }
        }}
        isContentHidden={isContentHidden}
        mindItemId={mindItemId}
        allowOnboarding={true}
      />
      <Box sx={{
        visibility: isReaderDetailsCollapsed ? 'visible' : 'hidden',
        position: isReaderDetailsCollapsed ? 'relative' : 'absolute',
      }}>
        <IconButton
          ref={buttonRef}
          onClick={expand}
          size='large'
          sx={{
            color: palette.text.primary,
          }}
        >
          <SvgIcon component={getMuiIcon('clipboardText')} />
        </IconButton>
      </Box>
    </Paper>
  </Box>;

  const withOnboarding = <OnboardingHint
    width={338}
    id={onboardingHints.newReaderDesktop}
    placement='right'
    isNew={true}
    title={t('Welcome to the new document reader!')}
    description={t('We have optimized the interface for you: ' +
      'Information about the unit and the entire work can now be found here. ' +
      'Would you like to learn more? ' +
      'Start the tour or check out our FAQ.')}
    actions={{
      primary: {
        label: t('Make a tour'),
        onClick: () => onboardingStepCounter?.startOnboarding(),
      },
      secondary: {
        label: t('Explore for yourself'),
        onClick: noop,
      },
    }}
  >
    {readerDetailsWrapper}
  </OnboardingHint>;

  return withOnboarding;
};

export default ReaderDetailsWrapper;
